import { Component, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { SubmitButtonComponent } from 'components/input/submit-button/submit-button.component';
import { ProductPlanTagConstants } from 'constants/ProductPlanTagConstants';
import { InputType } from 'enums/InputType';
import { SiteType } from 'enums/SiteType';
import { ValidationType } from 'enums/validationType';
import 'extensions/validation.ts';
import { SelectListItemModel } from 'models/SelectList';
import { Inputs } from 'modules/inputs';
import { Layout } from 'modules/layout';
import { ProductNamePipe } from 'pipes/product-name.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { AccountingService } from 'services/accounting.service';
import { AiBuilderService } from 'services/ai-builder.service';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { FormValidationService } from 'services/form-validation.service';
import { SiteService } from 'services/site.service';

@Component({
	selector: 'add-company-site',
	templateUrl: './add-company-site.component.html',
	providers: [FormValidationService],
	imports: [FormsModule, Layout, Inputs, forwardRef(() => SelectWebsiteProductPlanComponent), TranslatePipe]
})

export class AddCompanySiteComponent {

	@ViewChild('saveButton') saveButton!: SubmitButtonComponent;

	@Input() companyId?: string;
	validationType = ValidationType;
	inputTypeEnum = InputType;
	siteTypeEnum = SiteType;
	productPlanId?: string;
	pointerName = '';
	selectedType: SiteType = SiteType.WebserverApi;
	canSave = false;
	createSite = false;

	siteTypes = [SiteType.Dsa, SiteType.WebserverApi, SiteType.AiBuilder];
	constructor(private readonly siteService: SiteService, private readonly aiBuilderService: AiBuilderService, private readonly dataProviderFactory: CompanyDataProviderFactory) { }

	async save() {
		if (!this.productPlanId || !this.companyId) return;

		const newSite = await this.siteService.createSite(this.selectedType, this.pointerName, this.companyId, this.productPlanId);
		
		this.createSite = false;
		this.pointerName = '';
		this.saveButton.succeeded();

		if (newSite.type == SiteType.AiBuilder) {
			this.aiBuilderService.launchAiBuilder(newSite, this.dataProviderFactory.getProviderForCurrentScope());
		}
	}
}

@Component({
	selector: 'select-website-product-plan',
	template: '<custom-select [label]="\'site.select-plan\' | translate" [selectList]="webPlans" [(value)]="productPlanId" (valueChange)="productPlanIdChanged($event)" [required]="true"></custom-select>',
	imports: [CustomSelectComponent, TranslatePipe]
})
export class SelectWebsiteProductPlanComponent {

	@Output() public productPlanIdChange = new EventEmitter<string>();
	@Input() public productPlanId?: string;
	public webPlans: SelectListItemModel[] = [];
	public canChange = false;
	private dataProvider: CompanyDataProvider;
	private _companyId?: string;
	@Input()
	set companyId(id: string | undefined) {

		if (!id || id == '') return;
		this._companyId = id;
		this.getCompanyWebPlans();
	}
	get companyId() { return this._companyId; }

	constructor(
		dataProviderFactory: CompanyDataProviderFactory,
		private readonly productNamePipe: ProductNamePipe,
		private readonly accountingService: AccountingService
	) {
		this.dataProvider = dataProviderFactory.getProviderForCurrentScope();
	}

	productPlanIdChanged(newId: any) {
		this.productPlanIdChange.emit(newId);
	}

	getCompanyWebPlans() {
		if (!this.companyId) return;

		this.webPlans = [];
		this.dataProvider.getProductPlansByTag(ProductPlanTagConstants.web).then(webPlans => {
			webPlans = webPlans.filter(plan => this.accountingService.isActive(this.accountingService.filterActiveAccountingPlan(plan.accountingPlans)));
			this.webPlans = webPlans.map(plan => new SelectListItemModel(plan.id!, this.productNamePipe.transform(plan, true, true)));

			if (webPlans.length > 1) this.canChange = true;
			if (this.productPlanId) {
				if (webPlans.length === 1 && webPlans[0]?.id != this.productPlanId) this.canChange = true; // If there is only one valid web plan, but site is currently linked to an invalid one, allow change
			}
			else if (webPlans.length === 1) {
				this.productPlanId = webPlans[0].id!; // If there is only one web plan, user doesn't need to select
				this.productPlanIdChange.emit(this.productPlanId);
			}
		});
	}
}
